// What a phone's room header opens has a history entry while open, so the system's Back closes it and
// never leaves the chat (11, v1.58.76): the members page, the threads, the pinned messages, the room menu
// and the settings window. Each layer's entry is on top of the one it was opened from; closed by its own
// buttons, its entry goes again. A layer opening while one goes waits for that Back to land first.
import { useEffect, useRef } from 'react';

const layersOf = (): string[] => (history.state as { layers?: string[] } | null)?.layers ?? [];
let next = 0;
/** The layers' count history is going back to (Infinity while nothing goes). */
let pending = Infinity;
let landed: Promise<void> = Promise.resolve();
let land: (() => void) | undefined;
addEventListener('popstate', () => ((pending = Infinity), land?.(), (land = undefined)));

/** While `on`, Back calls `close`; closed otherwise, its history entry goes. */
export function useBackCloses(on: boolean, close: () => void): void {
  const closing = useRef(close);
  closing.current = close;
  useEffect(() => {
    if (!on) return;
    const id = `layer-${++next}`;
    let live = true;
    let pushed = false;
    const onPop = () => !layersOf().includes(id) && ((pushed = false), closing.current());
    void landed.then(() => {
      if (!live) return;
      history.pushState({ ...history.state, layers: [...layersOf(), id] }, '');
      pushed = true;
      addEventListener('popstate', onPop);
    });
    return () => {
      live = false;
      removeEventListener('popstate', onPop);
      const layers = layersOf();
      const at = layers.indexOf(id);
      if (!pushed || at < 0 || at >= pending) return;
      if (pending === Infinity) landed = new Promise((r) => (land = r));
      history.go(at - Math.min(layers.length, pending));
      pending = at;
    };
  }, [on]);
}
